import React from 'react';
import { Phone, Mail, MapPin, Linkedin, Instagram, Facebook, Twitter, ShieldCheck, HeartPulse, Building2, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ADVISOR_NAME, ADVISOR_PHONE, ADVISOR_PHONE_DISPLAY, ADVISOR_EMAIL } from './SEOHead';
import { trackPhoneClick } from '../services/analytics';

const Footer = () => {
  const { t, i18n } = useTranslation();
  const isTe = i18n.language === 'te';

  return (
    <footer className="bg-slate-950 text-slate-300 pt-16 pb-12 border-t border-slate-800 relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl opacity-50 -translate-y-1/2 pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl opacity-50 translate-y-1/2 pointer-events-none" />

      <div className="container mx-auto px-6 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-12 lg:gap-8">
          {/* Brand & Mission (Spans 4 cols) */}
          <div className="lg:col-span-4 flex flex-col gap-6">
            <Link to="/" className="flex items-center gap-3 group w-max">
              <img 
                src="/logo.png" 
                alt="VSR Insurance Plus Logo" 
                width="48" 
                height="48" 
                className="w-12 h-12 object-contain drop-shadow-sm group-hover:scale-105 transition-transform duration-300" 
              />
              <div className="flex flex-col">
                <span className="text-xl font-bold text-white uppercase tracking-wide">
                  VSR Insurance Plus
                </span>
                <span className="text-xs text-slate-400">
                  {isTe ? 'ఎస్‌విఆర్ ఎల్‌ఐసి సర్వీసెస్ • 2000 నుండి' : 'SVR LIC Services • Since 2000'}
                </span>
              </div>
            </Link>
            <p className="text-slate-400 text-sm leading-relaxed max-w-sm">
              {t('expert_advisory')}
            </p>
            <div className="flex gap-4 items-center">
              <div className="flex -space-x-3">
                <div className="w-10 h-10 rounded-full bg-slate-800 border-2 border-slate-950 flex items-center justify-center">
                  <ShieldCheck className="w-5 h-5 text-emerald-400" />
                </div>
                <div className="w-10 h-10 rounded-full bg-slate-800 border-2 border-slate-950 flex items-center justify-center">
                  <HeartPulse className="w-5 h-5 text-rose-400" />
                </div>
                <div className="w-10 h-10 rounded-full bg-slate-800 border-2 border-slate-950 flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-sky-400" />
                </div>
              </div>
              <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                {t('happy_families')}
              </span>
            </div>
          </div>

          {/* Quick Links (Spans 2 cols) */}
          <div className="lg:col-span-2">
            <h3 className="text-white font-semibold mb-6 flex items-center gap-2">
              {t('quick_links')}
            </h3>
            <ul className="space-y-3 text-sm">
              <li>
                <Link to="/" className="hover:text-primary transition-colors inline-block">{t('home')}</Link>
              </li>
              <li>
                <Link to="/about" className="hover:text-primary transition-colors inline-block">{t('about')}</Link>
              </li>
              <li>
                <Link to="/lic-plans" className="hover:text-primary transition-colors inline-block">{t('lic_plans')}</Link>
              </li>
              <li>
                <Link to="/health-insurance" className="hover:text-primary transition-colors inline-block">{t('health_plans')}</Link>
              </li>
              <li>
                <Link to="/nri-insurance" className="hover:text-primary transition-colors inline-block">{t('nri_insurance')}</Link>
              </li>
              <li>
                <Link to="/blog" className="hover:text-primary transition-colors inline-block">{t('insurance_guides')}</Link>
              </li>
              <li>
                <Link to="/contact" className="hover:text-primary transition-colors inline-block">{t('contact')}</Link>
              </li>
              <li>
                <Link to="/nri-calculator" className="text-orange-400 hover:text-orange-300 font-medium inline-block">
                  {t('nri_calculator')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Dedicated Insurance Portfolios (Spans 3 cols) */}
          <div className="lg:col-span-3">
            <h3 className="text-white font-semibold mb-6">
              {t('our_services')}
            </h3>
            <ul className="space-y-3 text-sm text-slate-400">
              <li>
                <Link to="/child-insurance-plans" className="hover:text-white transition-colors flex items-center gap-1.5">
                  <ArrowRight className="w-3 h-3 text-primary" /> {t('child_plans')}
                </Link>
              </li>
              <li>
                <Link to="/pension-retirement-plans" className="hover:text-white transition-colors flex items-center gap-1.5">
                  <ArrowRight className="w-3 h-3 text-primary" /> {t('pension_plans')}
                </Link>
              </li>
              <li>
                <Link to="/plan/lic-jeevan-utsav" className="hover:text-white transition-colors flex items-center gap-1.5">
                  <ArrowRight className="w-3 h-3 text-primary" /> {isTe ? 'ఎల్‌ఐసి జీవన్ ఉత్సవ్ (10% ఆదాయం)' : 'LIC Jeevan Utsav (10% Income)'}
                </Link>
              </li>
              <li>
                <Link to="/plan/lic-amrit-baal" className="hover:text-white transition-colors flex items-center gap-1.5">
                  <ArrowRight className="w-3 h-3 text-primary" /> {isTe ? 'ఎల్‌ఐసి అమృతబాల్ (చదువుల ప్లాన్)' : 'LIC Amritbaal Child Plan'}
                </Link>
              </li>
              <li>
                <Link to="/plan/lic-jeevan-shanti" className="hover:text-white transition-colors flex items-center gap-1.5">
                  <ArrowRight className="w-3 h-3 text-primary" /> {isTe ? 'ఎల్‌ఐసి న్యూ జీవన్ శాంతి (పెన్షన్)' : 'LIC New Jeevan Shanti Pension'}
                </Link>
              </li>
              <li>
                <Link to="/plan/hdfc-ergo-optima-secure" className="hover:text-white transition-colors flex items-center gap-1.5">
                  <ArrowRight className="w-3 h-3 text-primary" /> {isTe ? 'హెచ్‌డిఎఫ్‌సి ఎర్గో ఆప్టిమా సెక్యూర్' : 'HDFC ERGO Optima Secure (4X Cover)'}
                </Link>
              </li>
              <li>
                <Link to="/plan/hdfc-ergo-optima-senior" className="hover:text-white transition-colors flex items-center gap-1.5">
                  <ArrowRight className="w-3 h-3 text-primary" /> {isTe ? 'తల్లిదండ్రుల సీనియర్ సిటిజన్ హెల్త్' : 'Health Cover for Elderly Parents'}
                </Link>
              </li>
              <li>
                <Link to="/plan/lic-tech-term" className="hover:text-white transition-colors flex items-center gap-1.5">
                  <ArrowRight className="w-3 h-3 text-primary" /> {isTe ? 'ప్యూర్ టర్మ్ ఇన్సూరెన్స్ (కోటి రక్షణ)' : 'Pure Term Life Cover (₹1 Cr+)'}
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact & Office (Spans 3 cols) */}
          <div className="lg:col-span-3 flex flex-col gap-6">
            <h3 className="text-white font-semibold mb-2">{t('contact_us')}</h3>
            <div className="space-y-4 text-sm">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-full bg-slate-800 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Phone className="w-4 h-4 text-primary" />
                </div>
                <div className="flex flex-col">
                  <span className="text-slate-500 text-xs uppercase tracking-wider font-semibold">
                    {isTe ? 'కాల్ లేదా వాట్సాప్' : 'Call or WhatsApp'}
                  </span>
                  <a
                    href={`tel:${ADVISOR_PHONE}`}
                    onClick={() => trackPhoneClick('footer_phone')}
                    className="text-white font-medium hover:text-primary transition-colors text-base"
                  >
                    {ADVISOR_PHONE_DISPLAY}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-full bg-slate-800 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Mail className="w-4 h-4 text-primary" />
                </div>
                <div className="flex flex-col">
                  <span className="text-slate-500 text-xs uppercase tracking-wider font-semibold">
                    {t('email_inquiries')}
                  </span>
                  <a
                    href={`mailto:${ADVISOR_EMAIL}`}
                    className="text-white font-medium hover:text-primary transition-colors text-sm"
                  >
                    {ADVISOR_EMAIL}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-full bg-slate-800 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <MapPin className="w-4 h-4 text-primary" />
                </div>
                <div className="flex flex-col">
                  <span className="text-white font-medium">
                    {isTe ? 'హైదరాబాద్, తెలంగాణ, భారతదేశం' : 'Hyderabad, Telangana, India'}
                  </span>
                  <span className="text-slate-400 text-xs mt-0.5">
                    {isTe ? 'ఎస్‌విఆర్ ఎల్‌ఐసి సర్వీసెస్ • అఖిల భారత & ఎన్‌ఆర్‌ఐ సేవలు' : 'SVR LIC Services • Pan India & Global NRIs'}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex gap-4 mt-2">
              <a
                href="https://www.linkedin.com/in/veereddy-srinivas-reddy-23914a5a/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn profile of V. S. Reddy"
                className="text-slate-400 hover:text-sky-400 transition-colors p-2 bg-slate-900 rounded-full"
              >
                <Linkedin className="w-4 h-4" />
              </a>
              <a
                href="https://www.instagram.com/vsr_insurance_plus?igsh=MTJ5YWY3dDY4aGhiZw%3D%3D&utm_source=qr"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram page of VSR Insurance Plus"
                className="text-slate-400 hover:text-pink-400 transition-colors p-2 bg-slate-900 rounded-full"
              >
                <Instagram className="w-4 h-4" />
              </a>
              <a
                href="https://www.facebook.com/vsrinsuranceplus"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook page of VSR Insurance Plus"
                className="text-slate-400 hover:text-blue-500 transition-colors p-2 bg-slate-900 rounded-full"
              >
                <Facebook className="w-4 h-4" />
              </a>
              <a
                href="https://twitter.com/vsrinsurance"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Twitter handle of VSR Insurance Plus"
                className="text-slate-400 hover:text-sky-400 transition-colors p-2 bg-slate-900 rounded-full"
              >
                <Twitter className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>

        {/* Regulatory & Section 41 Notice */}
        <div className="mt-12 pt-8 border-t border-slate-800/80 text-xs text-slate-400 leading-relaxed text-center max-w-4xl mx-auto space-y-2">
          <p>{t('regulatory_notice')}</p>
        </div>

        {/* Bottom Bar & Legal Links */}
        <div className="mt-8 pt-6 border-t border-slate-900 flex flex-col sm:flex-row justify-between items-center gap-4 text-xs text-slate-400">
          <div>
            &copy; {new Date().getFullYear()} VSR Insurance Plus ({isTe ? 'వి. ఎస్. రెడ్డి' : 'V. S. Reddy'}). {t('all_rights_reserved')}
          </div>
          <div className="flex gap-6 items-center">
            <Link to="/privacy" className="hover:text-primary transition-colors">{t('privacy_policy')}</Link>
            <span className="text-slate-700">•</span>
            <Link to="/terms" className="hover:text-primary transition-colors">{t('terms_of_service')}</Link>
            <span className="text-slate-700">•</span>
            <Link to="/disclaimer" className="hover:text-primary transition-colors">{t('disclaimer')}</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
